import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheck, FiStar, FiShield, FiAward, FiClock, FiHeart, FiTool, FiUsers, FiTrendingUp, FiThumbsUp } = FiIcons;

const WhyUs = () => {
  const advantages = [
    {
      icon: FiShield,
      title: 'Fully Insured & Licensed',
      description: 'Complete peace of mind with comprehensive public liability insurance and all necessary certifications.',
      details: ['£2 million public liability insurance', 'Waste carrier licence', 'Health & safety certified']
    },
    {
      icon: FiAward,
      title: 'Professional Equipment',
      description: 'Investment in top-quality pressure washing and cleaning equipment for superior results.',
      details: ['Commercial-grade pressure washers', 'Professional surface cleaners', 'Eco-friendly cleaning products']
    },
    {
      icon: FiUsers,
      title: 'Local & Trusted',
      description: 'Based in your community, serving Malton, Norton, Pickering with personalised service.',
      details: ['Local business', 'Established reputation', 'Word-of-mouth referrals']
    },
    {
      icon: FiClock,
      title: 'Reliable & Punctual',
      description: 'Respect for your time with prompt service and clear communication throughout.',
      details: ['On-time arrival guaranteed', 'Clear scheduling', 'Progress updates provided']
    },
    {
      icon: FiTool,
      title: 'Expert Knowledge',
      description: 'Professional experience handling all types of surfaces and cleaning challenges.',
      details: ['Surface-specific techniques', 'Stain removal expertise', 'Damage prevention methods']
    },
    {
      icon: FiHeart,
      title: 'Customer Satisfaction',
      description: 'Commitment to exceeding expectations with every job, backed by our satisfaction guarantee.',
      details: ['100% satisfaction guarantee', 'After-service support', 'Competitive pricing']
    }
  ];

  const testimonials = [
    {
      name: 'Sarah',
      location: 'Malton',
      rating: 5,
      comment: 'Absolutely brilliant service! My patio looks brand new again. Professional, punctual, and reasonably priced.',
      service: 'Patio Cleaning'
    },
    {
      name: 'Michael',
      location: 'Norton',
      rating: 5,
      comment: 'Outstanding work on our driveway. The difference is incredible. Will definitely use again and recommend to others.',
      service: 'Pressure Washing'
    },
    {
      name: 'Emma',
      location: 'Pickering',
      rating: 5,
      comment: 'Fantastic job cleaning our outdoor surfaces. Very professional approach and excellent results. Highly recommended!',
      service: 'Surface Cleaning'
    }
  ];

  const stats = [
    { number: '100+', label: 'Happy Customers' },
    { number: '1+', label: 'Year Established' },
    { number: '100%', label: 'Satisfaction Rate' },
    { number: '24hr', label: 'Response Time' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">Why Choose Us?</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Discover what makes Jet'n'Seal the trusted choice for patio cleaning and pressure washing 
              services across North Yorkshire.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl lg:text-5xl font-bold text-blue-600 mb-2">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Advantages */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              The Jet'n'Seal Advantage
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Here's what sets us apart from other patio cleaning and pressure washing services in the area.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {advantages.map((advantage, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <SafeIcon icon={advantage.icon} className="w-12 h-12 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{advantage.title}</h3>
                    <p className="text-gray-600 mb-4">{advantage.description}</p>
                    <ul className="space-y-2">
                      {advantage.details.map((detail, detailIndex) => (
                        <li key={detailIndex} className="flex items-center space-x-2">
                          <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span className="text-sm text-gray-700">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Professional Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach ensuring consistent, high-quality results every time.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: '1',
                title: 'Initial Assessment',
                description: 'Thorough inspection of surfaces and assessment of cleaning requirements'
              },
              {
                step: '2',
                title: 'Preparation',
                description: 'Proper setup, protection of plants and property, safety measures in place'
              },
              {
                step: '3',
                title: 'Professional Cleaning',
                description: 'Using appropriate techniques and equipment for optimal results'
              },
              {
                step: '4',
                title: 'Quality Check',
                description: 'Final inspection and touch-ups to ensure complete satisfaction'
              }
            ].map((process, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {process.step}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{process.title}</h3>
                <p className="text-gray-600 text-sm">{process.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Our Customers Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real feedback from satisfied customers across Malton, Norton, Pickering and surrounding areas.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="bg-white p-6 rounded-xl shadow-lg"
              >
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <SafeIcon key={i} icon={FiStar} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">"{testimonial.comment}"</p>
                <div className="border-t pt-4">
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-600">{testimonial.location}</div>
                  <div className="text-sm text-blue-600 font-medium">{testimonial.service}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <h2 className="text-4xl font-bold">
              Ready to Experience the Difference?
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Join our growing list of satisfied customers who trust Jet'n'Seal for their patio cleaning 
              and pressure washing needs.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact-us"
                className="bg-white text-blue-800 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors duration-200"
              >
                Get Your Free Quote
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default WhyUs;
